import React, { createContext, useContext, useEffect, ReactNode, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { DEFAULT_LANGUAGE, Language, localizedPath, stripLanguage } from '../constants/routes';
import {
  clearCategory,
  readStoredItem,
  subscribeToConsentChange,
  writeStoredItem,
} from '../utils/consentStorage';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  resetLanguage: () => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
}

const readStoredLanguage = (): Language | null => {
  const saved = readStoredItem('language');
  return saved === 'de' || saved === 'en' ? saved : null;
};

// Persisting the language is its own consent category, so it needs the
// visitor's agreement to that category - not merely to cookies in general.
const storeLanguage = (lang: Language | null) => {
  if (lang === null) {
    clearCategory('preferences');
    return;
  }
  writeStoredItem('preferences', 'language', lang);
};

/**
 * The URL decides the language, never the browser: /about is German, /en/about
 * is English, and both stay that way for every visitor and every crawler.
 *
 * The single exception is the bare domain. Someone who explicitly picked a
 * language earlier is sent to it, so that typing me-support.net does not throw
 * them back into the language they just left. Visitors without a stored choice
 * - including crawlers, which have none - are never redirected.
 */

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();

  // The URL is the source of truth: no prefix means the default language.
  const { language: urlLanguage, path } = stripLanguage(location.pathname);
  const language = urlLanguage ?? DEFAULT_LANGUAGE;

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  // Only an explicit prefix counts as a choice worth remembering - storing the
  // derived default here would overwrite a choice the visitor already made.
  useEffect(() => {
    if (!urlLanguage) {
      return;
    }
    storeLanguage(urlLanguage);
    // Consent given later still applies to the language already on screen,
    // instead of waiting for the next navigation. Withdrawing it is handled by
    // the consent provider, which clears the whole category.
    return subscribeToConsentChange(() => storeLanguage(urlLanguage));
  }, [urlLanguage]);

  // Only the bare domain honours a previously chosen language.
  useEffect(() => {
    if (location.pathname !== '/') {
      return;
    }
    const stored = readStoredLanguage();
    if (stored && stored !== DEFAULT_LANGUAGE) {
      navigate(`${localizedPath('/', stored)}${location.search}${location.hash}`, { replace: true });
    }
  }, [location.pathname, location.search, location.hash, navigate]);

  const setLanguage = useCallback(
    (lang: Language) => {
      storeLanguage(lang);
      navigate(`${localizedPath(path, lang)}${location.search}${location.hash}`);
    },
    [navigate, path, location.search, location.hash]
  );

  const resetLanguage = useCallback(() => {
    storeLanguage(null);
    navigate(`${localizedPath(path, DEFAULT_LANGUAGE)}${location.search}${location.hash}`);
  }, [navigate, path, location.search, location.hash]);

  const t = useCallback(
    (key: string): string => {
      // Simple translation function - you can expand this as needed
      const translations: Record<string, Record<Language, string>> = {
        'language.en': { en: 'English', de: 'Englisch' },
        'language.de': { en: 'German', de: 'Deutsch' },
        'nav.home': { en: 'Home', de: 'Startseite' },
        'nav.about': { en: 'About Us', de: 'Über uns' },
        'nav.whatIsME': { en: 'What is ME', de: 'Was ist ME' },
        'nav.mitmachen': { en: 'Join Us', de: 'Mitmachen' },
        'nav.instagram': { en: 'Instagram', de: 'Instagram' },
        'nav.donate': { en: 'Donate', de: 'Spenden' },
        'nav.impressum': { en: 'Legal Notice', de: 'Impressum' },
        'nav.privacy': { en: 'Privacy Policy', de: 'Datenschutz' },
        'nav.cookies': { en: 'Cookie Policy', de: 'Cookie-Erklärung' },
      };

      const translation = translations[key];
      if (translation) {
        return translation[language] || key;
      }

      return key;
    },
    [language]
  );

  return (
    <LanguageContext.Provider value={{ language, setLanguage, resetLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
